import {
  AddNotionIntegrationProjectRequestConfig,
  BeginNotionWebhookVerificationRequestConfig,
  CreateNotionIntegrationConfigRequestConfig,
  DeleteNotionIntegrationConfigRequestConfig,
  type NotionDatabasePage,
  NotionDatabasePagesRequestConfig,
  NotionDatabaseViewsRequestConfig,
  type NotionDiscoverNode,
  NotionDiscoverRequestConfig,
  type NotionIntegrationConfig,
  NotionIntegrationConfigsRequestConfig,
  type NotionIntegrationConfigsResponse,
  NotionProjectDatabasePagesRequestConfig,
  NotionProjectDiscoverRequestConfig,
  NotionProjectIntegrationRequestConfig,
  type NotionSyncMode,
  type NotionView,
  NotionViewCountRequestConfig,
  type NotionViewCountResponse,
  type NotionWebhookInfo,
  NotionWebhookRequestConfig,
  RemoveNotionIntegrationProjectRequestConfig,
  RotateNotionIntegrationTokenRequestConfig,
  UpdateNotionSyncModeRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { err, ok, type Result } from "neverthrow";
import { createSignal } from "solid-js";
import { isServer } from "solid-js/web";
import { type FeatureAction, trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

export type {
  NotionDiscoverNode,
  NotionIntegrationConfig,
  NotionSyncMode,
  NotionView,
  NotionWebhookInfo,
} from "@storytell/client";

interface CreateNotionIntegrationInput {
  botToken: string;
  projectIds: string[];
}

// Wraps the server error envelope as an Error, keeping its code for translation.
class NotionIntegrationError extends Error {
  readonly errorResult: { code?: string | number; message?: string; statusCode?: number };

  constructor(payload: { code?: string | number; message?: string; statusCode?: number }) {
    const message = payload.message ?? "Request failed";
    super(message);
    this.name = "NotionIntegrationError";
    this.errorResult = {
      code: payload.code,
      message,
      statusCode: payload.statusCode,
    };
  }
}

const toError = (e: Error): NotionIntegrationError => {
  const meta = e as Error & { code?: string; status?: number };
  return new NotionIntegrationError({
    code: meta.code ?? meta.status,
    message: e.message,
    statusCode: meta.status,
  });
};

export function useNotionIntegration() {
  const wire = useWire();
  const client = useStorytellClient();

  const [isSaving, setIsSaving] = createSignal(false);
  const [error, setError] = createSignal<NotionIntegrationError | null>(null);

  // Owns the saving flag and error routing so each write only supplies its fetch.
  const mutate = async <T>(
    doFetch: () => Promise<Result<T, Error>>,
    action: FeatureAction<"integration.notion">
  ): Promise<Result<T, NotionIntegrationError>> => {
    if (isServer) return err(new NotionIntegrationError({ statusCode: 0 }));
    setError(null);
    setIsSaving(true);
    try {
      const res = await doFetch();
      if (res.isErr()) {
        const e = toError(res.error);
        setError(e);
        return err(e);
      }
      trackFeature({ feature: "integration.notion", action, via: "button" });
      return ok(res.value);
    } finally {
      setIsSaving(false);
    }
  };

  const emptyList: NotionIntegrationConfigsResponse = { integrations: [] };

  const listConfigs = async (): Promise<Result<NotionIntegrationConfigsResponse, NotionIntegrationError>> => {
    if (isServer) return ok(emptyList);
    const res = await client.fetch(
      NotionIntegrationConfigsRequestConfig.withBearerToken(wire.auth.token()),
      undefined,
      undefined,
      {
        orgIdOverride: wire.currentOrgId(),
      }
    );
    if (res.isErr()) {
      // Grants maps out-of-scope reads to 404, so non-admins get an empty list
      // instead of an error.
      if ((res.error as Error & { status?: number }).status === 404) return ok(emptyList);
      return err(toError(res.error));
    }
    return ok(res.value);
  };

  const createConfig = (
    input: CreateNotionIntegrationInput
  ): Promise<Result<NotionIntegrationConfig, NotionIntegrationError>> =>
    mutate(
      () =>
        client.fetch(
          CreateNotionIntegrationConfigRequestConfig.withBearerToken(wire.auth.token()),
          undefined,
          { botToken: input.botToken, projectIds: input.projectIds },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "connect"
    );

  const deleteConfig = (integrationId: string): Promise<Result<void, NotionIntegrationError>> =>
    mutate(
      () =>
        client.fetch(
          DeleteNotionIntegrationConfigRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          undefined,
          {
            orgIdOverride: wire.currentOrgId(),
          }
        ),
      "disconnect"
    );

  // An empty selectedRootIds deploys a project that syncs nothing until roots are picked.
  const addProject = (
    integrationId: string,
    projectId: string,
    selectedRootIds: string[]
  ): Promise<Result<NotionIntegrationConfig, NotionIntegrationError>> =>
    mutate(
      () =>
        client.fetch(
          AddNotionIntegrationProjectRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          { projectId, selectedRootIds },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "deploy"
    );

  // The backend verifies the token against Notion before storing it, clears
  // authBrokenAt, and queues a full re-sync of every deployment.
  const rotateToken = (
    integrationId: string,
    botToken: string
  ): Promise<Result<NotionIntegrationConfig, NotionIntegrationError>> =>
    mutate(
      () =>
        client.fetch(
          RotateNotionIntegrationTokenRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          { botToken },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "reconnect"
    );

  const removeProject = (integrationId: string, projectId: string): Promise<Result<void, NotionIntegrationError>> =>
    mutate(
      () =>
        client.fetch(
          RemoveNotionIntegrationProjectRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId, projectId },
          undefined,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "undeploy"
    );

  // allowPrune must be true for a narrowing change; the backend rejects an
  // unconfirmed narrowing with 409.
  const updateSyncMode = (
    integrationId: string,
    projectId: string,
    input: {
      syncMode: NotionSyncMode;
      selectedRootIds: string[];
      allowPrune: boolean;
      rootViews?: Record<string, string[]>;
    }
  ): Promise<Result<void, NotionIntegrationError>> =>
    mutate(
      () =>
        client.fetch(
          UpdateNotionSyncModeRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId, projectId },
          input,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "configure"
    );

  const databaseViews = async (
    integrationId: string,
    databaseId: string
  ): Promise<Result<NotionView[], NotionIntegrationError>> => {
    if (isServer) return ok([]);
    const res = await client.fetch(
      NotionDatabaseViewsRequestConfig.withBearerToken(wire.auth.token()),
      { integrationId, databaseId },
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value.views);
  };

  const viewRowCount = async (
    integrationId: string,
    databaseId: string,
    viewId: string
  ): Promise<Result<NotionViewCountResponse, NotionIntegrationError>> => {
    if (isServer) return ok({ count: 0, truncated: false });
    const res = await client.fetch(
      NotionViewCountRequestConfig.withBearerToken(wire.auth.token()),
      { integrationId, databaseId, viewId },
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value);
  };

  const discover = async (integrationId: string): Promise<Result<NotionDiscoverNode[], NotionIntegrationError>> => {
    if (isServer) return ok([]);
    const res = await client.fetch(
      NotionDiscoverRequestConfig.withBearerToken(wire.auth.token()),
      { integrationId },
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value.nodes);
  };

  const databasePages = async (
    integrationId: string,
    databaseId: string
  ): Promise<Result<NotionDatabasePage[], NotionIntegrationError>> => {
    if (isServer) return ok([]);
    const res = await client.fetch(
      NotionDatabasePagesRequestConfig.withBearerToken(wire.auth.token()),
      { integrationId, databaseId },
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value.pages);
  };

  // Project-member reads (any member of a project the integration is deployed
  // to): scoped by the project header, not org-owner-gated like the config reads.

  // A read, not a write; callers own the loading signal. 404 means the project
  // has no active deployment or the caller has no role in it — both map to
  // not-connected (null).
  const projectIntegration = async (): Promise<
    Result<{ authBrokenAt?: string | null } | null, NotionIntegrationError>
  > => {
    if (isServer) return ok(null);
    const res = await client.fetch(
      NotionProjectIntegrationRequestConfig.withBearerToken(wire.auth.token()),
      undefined,
      undefined,
      { projectIdOverride: wire.currentProjectId() }
    );
    if (res.isErr()) {
      if ((res.error as Error & { status?: number }).status === 404) return ok(null);
      return err(toError(res.error));
    }
    return ok(res.value);
  };

  const discoverProject = async (): Promise<Result<NotionDiscoverNode[], NotionIntegrationError>> => {
    if (isServer) return ok([]);
    const res = await client.fetch(
      NotionProjectDiscoverRequestConfig.withBearerToken(wire.auth.token()),
      undefined,
      undefined,
      { projectIdOverride: wire.currentProjectId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value.nodes);
  };

  const databasePagesProject = async (
    databaseId: string
  ): Promise<Result<NotionDatabasePage[], NotionIntegrationError>> => {
    if (isServer) return ok([]);
    const res = await client.fetch(
      NotionProjectDatabasePagesRequestConfig.withBearerToken(wire.auth.token()),
      { databaseId },
      undefined,
      { projectIdOverride: wire.currentProjectId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value.pages);
  };

  // The public webhook only stores Notion's handshake token while a verification
  // window is open, so callers poll this until verifyOpenUntil empties or the
  // token changes.
  const getWebhook = async (integrationId: string): Promise<Result<NotionWebhookInfo, NotionIntegrationError>> => {
    if (isServer) return ok({ webhookUrl: "", verificationToken: "", verifyOpenUntil: "", recommendedApiVersion: "" });
    const res = await client.fetch(
      NotionWebhookRequestConfig.withBearerToken(wire.auth.token()),
      { integrationId },
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value);
  };

  // Opens the verification window; the returned verifyOpenUntil tells the UI how
  // long Notion has to (re)create the subscription before incoming tokens are
  // ignored again.
  const beginWebhookVerification = async (
    integrationId: string
  ): Promise<Result<NotionWebhookInfo, NotionIntegrationError>> => {
    if (isServer) return ok({ webhookUrl: "", verificationToken: "", verifyOpenUntil: "", recommendedApiVersion: "" });
    const res = await client.fetch(
      BeginNotionWebhookVerificationRequestConfig.withBearerToken(wire.auth.token()),
      { integrationId },
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value);
  };

  return {
    listConfigs,
    createConfig,
    deleteConfig,
    rotateToken,
    addProject,
    removeProject,
    updateSyncMode,
    discover,
    databasePages,
    projectIntegration,
    discoverProject,
    databasePagesProject,
    databaseViews,
    viewRowCount,
    getWebhook,
    beginWebhookVerification,
    isSaving,
    error,
    clearError: () => setError(null),
  };
}
